import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp, 
  writeBatch,
  increment,
  limit
} from "firebase/firestore";
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  listAll
} from "firebase/storage";
import JSZip from "jszip";
import { db, app, storage } from "../lib/firebase";
import { 
  Flashcard,
  FlashcardProgress,
  Deck, 
  LeitnerBox, 
  UserProgress, 
  StudySession,
  CardSide
} from "../types";

// Decks Service
export const deckService = {
  getDecks: async (userId: string) => {
    const q = query(collection(db, "decks"), where("userId", "==", userId), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Deck));
  },

  getPublicDecks: async () => {
    const q = query(collection(db, "decks"), where("isPublic", "==", true), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Deck));
  },

  getDeck: async (id: string) => {
    const docRef = doc(db, "decks", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Deck;
    }
    return null;
  },

  createDeck: async (userId: string, deckData: Partial<Deck>) => {
    const docRef = await addDoc(collection(db, "decks"), {
      ...deckData,
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  },

  updateDeck: async (id: string, deckData: Partial<Deck>) => {
    const docRef = doc(db, "decks", id);
    return await updateDoc(docRef, {
      ...deckData,
      updatedAt: serverTimestamp(),
    });
  },

  deleteDeck: async (id: string) => {
    // 1. Delete all cards and progress
    const cards = await cardService.getCardsByDeck(id);
    const CHUNK_SIZE = 5; // Low limit to avoid 10 get() calls limit per batch in Firestore rules for old cards

    if (cards.length === 0) {
      const batch = writeBatch(db);
      batch.delete(doc(db, "decks", id));
      await batch.commit();
    } else {
      for (let i = 0; i < cards.length; i += CHUNK_SIZE) {
        const chunk = cards.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        
        // Delete progress for all cardIds in chunk
        const progressPromises = chunk.map(async (card) => {
          const q = query(collection(db, "cardProgress"), where("cardId", "==", card.id));
          const snapshot = await getDocs(q);
          snapshot.forEach(doc => batch.delete(doc.ref));
        });
        await Promise.all(progressPromises);

        // Delete cards in chunk
        chunk.forEach(card => {
          batch.delete(doc(db, "cards", card.id!));
        });

        // Delete the deck itself on the last chunk
        if (i + CHUNK_SIZE >= cards.length) {
          batch.delete(doc(db, "decks", id));
        }
        
        await batch.commit();
      }
    }

    // 2. Clean up storage assets (Best effort)
    try {
      const folderRef = ref(storage, `decks/${id}`);
      const listResult = await listAll(folderRef);
      
      // Recursive delete for images/audio folders
      const deleteRecursive = async (refPrefix: any) => {
        const list = await listAll(refPrefix);
        const subFolderDeletes = list.prefixes.map(p => deleteRecursive(p));
        const fileDeletes = list.items.map(i => deleteObject(i));
        await Promise.all([...subFolderDeletes, ...fileDeletes]);
      };
      
      await deleteRecursive(folderRef);
    } catch (e) {
      console.warn("Could not completely clean up storage assets for deck:", id, e);
    }

    return true;
  },

  cloneDeck: async (userId: string, sourceDeckId: string) => {
    // 1. Get source deck
    const sourceDeck = await deckService.getDeck(sourceDeckId);
    if (!sourceDeck) throw new Error("Source deck not found");

    // 2. Create new deck
    const { id: _sourceId, ...deckData } = sourceDeck;
    const newDeckRef = await addDoc(collection(db, "decks"), {
      ...deckData,
      userId,
      isPublic: false,
      cloneCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Update source deck clone count
    await updateDoc(doc(db, "decks", sourceDeckId), {
      cloneCount: increment(1)
    });

    // 3. Get all source cards
    const sourceCards = await cardService.getCardsByDeck(sourceDeckId);

    // 4. Batch create new cards
    const batch = writeBatch(db);
    sourceCards.forEach(card => {
      const newCardRef = doc(collection(db, "cards"));
      batch.set(newCardRef, {
        deckId: newDeckRef.id,
        userId: userId,
        front: card.front,
        back: card.back,
        tags: card.tags || [],
        box: card.box ?? 1,
        nextReview: card.nextReview ?? serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    await batch.commit();
    return newDeckRef.id;
  }
};

// Cards Service
export const cardService = {
  createCard: async (deckId: string, card: Partial<Flashcard>) => {
    const { id: _cardId, ...cardData } = card;
    const cardsRef = collection(db, "cards");
    const docRef = await addDoc(cardsRef, {
      ...cardData,
      deckId,
      userId: (await deckService.getDeck(deckId))?.userId, // Fallback to fetching deck's userId
      box: card.box ?? 1,
      nextReview: card.nextReview ?? serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef;
  },

  getCardsByDeck: async (deckId: string) => {
    const q = query(collection(db, "cards"), where("deckId", "==", deckId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Flashcard));
  },

  getCard: async (id: string) => {
    const docRef = doc(db, "cards", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Flashcard;
    }
    return null;
  },

  updateCard: async (id: string, data: Partial<Flashcard>) => {
    const docRef = doc(db, "cards", id);
    return await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  deleteCard: async (id: string) => {
    return await deleteDoc(doc(db, "cards", id));
  }
};

// Storage Service
export const storageService = {
  uploadFile: async (file: File, path: string) => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  },

  deleteFile: async (path: string) => {
    const storageRef = ref(storage, path);
    return await deleteObject(storageRef);
  },

  uploadZipAssets: async (zipFile: File, basePath: string) => {
    const zip = new JSZip();
    const contents = await zip.loadAsync(zipFile);
    const assetMap: Record<string, string> = {};

    for (const [filename, file] of Object.entries(contents.files)) {
      if (file.dir) continue;
      
      const blob = await file.async("blob");
      const path = `${basePath}/${Date.now()}_${filename}`;
      const url = await storageService.uploadFile(blob as File, path);
      assetMap[filename] = url;
    }

    return assetMap;
  }
};

// Leitner Boxes
export const LEITNER_BOXES: LeitnerBox[] = [
  { id: 1, name: "Box 1", intervalDays: 1 },
  { id: 2, name: "Box 2", intervalDays: 3 },
  { id: 3, name: "Box 3", intervalDays: 7 },
  { id: 4, name: "Box 4", intervalDays: 14 },
  { id: 5, name: "Box 5", intervalDays: 30 },
];

// User Progress Service
export const userProgressService = {
  getUserProgress: async (userId: string) => {
    const docRef = doc(db, "userProgress", userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as any as UserProgress;
    }
    return null;
  },

  updatePoints: async (userId: string, pointsToAdd: number) => {
    const docRef = doc(db, "userProgress", userId);
    return await updateDoc(docRef, {
      points: increment(pointsToAdd),
      updatedAt: serverTimestamp(),
    });
  }
};

// Card Progress Service (SRS tracking)
export const cardProgressService = {
  getCardProgress: async (userId: string, cardId: string) => {
    const q = query(
      collection(db, "cardProgress"), 
      where("userId", "==", userId), 
      where("cardId", "==", cardId),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as FlashcardProgress & { id: string };
  },

  getAllProgressForDeck: async (userId: string, deckId: string) => {
    const q = query(
      collection(db, "cardProgress"), 
      where("userId", "==", userId), 
      where("deckId", "==", deckId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as (FlashcardProgress & { id: string })[];
  },

  getAllProgressForUser: async (userId: string) => {
    const q = query(
      collection(db, "cardProgress"), 
      where("userId", "==", userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as (FlashcardProgress & { id: string })[];
  },

  updateCardProgress: async (progress: Partial<FlashcardProgress> & { userId: string, cardId: string, deckId: string }) => {
    const existing = await cardProgressService.getCardProgress(progress.userId, progress.cardId);
    
    const { id: _id, ...progressData } = progress;
    const data = {
      ...progressData,
      updatedAt: serverTimestamp(),
    };

    if (existing) {
      const docRef = doc(db, "cardProgress", (existing as any).id);
      return await updateDoc(docRef, data);
    } else {
      return await addDoc(collection(db, "cardProgress"), {
        ...data,
        createdAt: serverTimestamp(),
      });
    }
  }
};
